import PropTypes from 'prop-types'
import { classNames } from '../../helpers'
import { useCopyToClipboard } from '../../hooks/use-copy-to-clipboard'
import { AnimatedCopyIcon } from '../motion'
import Badge from './Badge'

/**
 * Einheitliche, wiederverwendbare Detail-Zeile für die Talent-Detailseite.
 * Label (gedämpft, optional mit Icon) über gut lesbarem Wert.
 *
 * Wert-Varianten (Priorität): children > badge > href > copyable > value.
 */
const DetailField = ({
  icon: Icon,
  label,
  value,
  caption,
  href,
  badge,
  badgeColor,
  copyable,
  onClick,
  className,
  children
}) => {
  const { copied, copy } = useCopyToClipboard()

  const handleCopy = (e) => {
    e.stopPropagation()
    if (!value) return
    copy(value)
  }

  const renderValue = () => {
    if (children) return children

    if (badge) {
      return <Badge text={String(value)} color={badgeColor} type='basic' />
    }

    if (href) {
      return (
        <a
          href={href}
          onClick={(e) => e.stopPropagation()}
          className='text-base font-medium text-primaryBlue hover:text-primaryBlue/80'
        >
          {value}
        </a>
      )
    }

    if (copyable) {
      return (
        <button
          type='button'
          onClick={handleCopy}
          className='group/copy flex max-w-full items-center gap-1.5 text-left'
        >
          <span className='truncate text-base font-medium text-gray-900'>
            {value}
          </span>
          <AnimatedCopyIcon
            copied={copied}
            className='size-4 text-gray-400 group-hover/copy:text-gray-600'
          />
        </button>
      )
    }

    return (
      <dd
        title={typeof value === 'string' ? value : ''}
        className={classNames(
          'truncate text-base font-medium',
          onClick
            ? 'text-primaryBlue group-hover:text-primaryBlue/80'
            : 'text-gray-900'
        )}
      >
        {value}
      </dd>
    )
  }

  return (
    <div
      onClick={onClick}
      className={classNames(
        'min-w-0',
        onClick && 'group cursor-pointer',
        className
      )}
    >
      <dt className='flex items-center gap-1.5 text-sm text-gray-500'>
        {Icon && <Icon className='size-4 flex-shrink-0 text-primaryBlue' />}
        {label}
      </dt>
      <div className='mt-1'>{renderValue()}</div>
      {caption && <p className='mt-0.5 text-xs text-gray-400'>{caption}</p>}
    </div>
  )
}

DetailField.propTypes = {
  icon: PropTypes.elementType,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  caption: PropTypes.string,
  href: PropTypes.string,
  badge: PropTypes.bool,
  badgeColor: PropTypes.string,
  copyable: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node
}

DetailField.defaultProps = {
  icon: undefined,
  value: undefined,
  caption: undefined,
  href: undefined,
  badge: false,
  badgeColor: 'gray',
  copyable: false,
  onClick: undefined,
  className: undefined,
  children: undefined
}

export default DetailField
